import React, { useState, useEffect, useCallback, useRef } from 'react';
import { onAuthChange, loginUser } from './services/authService';
import * as firestoreService from './services/firestoreService';
import AppContext from './contexts/AppContext';
import useTheme from './hooks/useTheme';
import { NOTIFICATION_CATEGORY_MAP, DEFAULT_NOTIFICATION_PREFS } from './utils/constants';
import LoginScreen from './components/auth/LoginScreen';
import ProfileCompletionScreen from './components/auth/ProfileCompletionScreen';
import RestrictedScreen from './components/auth/RestrictedScreen';
import MainApp from './components/navigation/MainApp';
import TermsOfUseView from './components/legal/TermsOfUseView';
import PrivacyPolicyView from './components/legal/PrivacyPolicyView';
import CookiesPolicyView from './components/legal/CookiesPolicyView';
import LegalView from './components/legal/LegalView';
import AuthActionScreen from './components/auth/AuthActionScreen';
import MaintenancePage from './components/common/MaintenancePage';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import { initServicesFromConsent } from './utils/cookieConsent';

initServicesFromConsent();

const TRANSIENT_VIEWS = ['live-score', 'game-recap'];

const pathToView = (pathname) => {
  const path = pathname === '/' ? 'feed' : pathname.slice(1);
  return TRANSIENT_VIEWS.includes(path) ? 'feed' : (path || 'feed');
};

const App = () => {
  const [currentUser, setCurrentUserState] = useState(null);
  const setCurrentUser = (user) => {
    setCurrentUserState(user);
    if (user) {
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('currentUser');
    }
  };
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reports, setReports] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setViewState] = useState(() => {
    // Prefer URL path over localStorage (enables direct links)
    const fromUrl = pathToView(window.location.pathname);
    if (fromUrl !== 'feed') return fromUrl;
    const saved = localStorage.getItem('loft_view');
    return saved && !TRANSIENT_VIEWS.includes(saved) ? saved : 'feed';
  });

  const setView = useCallback((newView) => {
    setViewState(newView);
    const path = newView === 'feed' ? '/' : `/${newView}`;
    if (window.location.pathname !== path) {
      window.history.pushState({ view: newView }, '', path);
    }
  }, []);
  const [messagesKey, setMessagesKey] = useState(0);
  const [videoFiles, setVideoFiles] = useState({});
  const [imageFiles, setImageFiles] = useState({});
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [wordFilters, setWordFilters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [swings, setSwings] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const { theme, effectiveTheme, setTheme } = useTheme();
  const unsubscribesRef = useRef([]);
  const prevNotifIdsRef = useRef(new Set());
  const [maintenanceConfig, setMaintenanceConfig] = useState(null);
  const maintenanceTimerRef = useRef(null);
  const [searchNav, setSearchNav] = useState(null); // { tab: 'posts'|'users', query: string }

  // Persist view to localStorage and sync URL with browser back/forward
  useEffect(() => { localStorage.setItem('loft_view', view); }, [view]);

  useEffect(() => {
    const handlePopState = () => setViewState(pathToView(window.location.pathname));
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Maintenance mode real-time listener
  useEffect(() => {
    const unsub = firestoreService.onMaintenanceChange((config) => {
      clearTimeout(maintenanceTimerRef.current);
      if (config?.enabled && config?.minutes && config?.enabledAt) {
        const expiresAt = new Date(config.enabledAt).getTime() + config.minutes * 60 * 1000;
        const remaining = expiresAt - Date.now();
        if (remaining <= 0) {
          // Already expired — auto-disable
          firestoreService.setAppConfig('maintenance', { enabled: false }).catch(() => {});
          setMaintenanceConfig({ ...config, enabled: false });
          return;
        }
        maintenanceTimerRef.current = setTimeout(() => {
          firestoreService.setAppConfig('maintenance', { enabled: false }).catch(() => {});
          setMaintenanceConfig(prev => prev ? { ...prev, enabled: false } : prev);
        }, remaining);
      }
      setMaintenanceConfig(config);
    });
    return () => { unsub(); clearTimeout(maintenanceTimerRef.current); };
  }, []);

  // Custom modal system (replaces browser alert/confirm)
  const [modalState, setModalState] = useState(null);
  const modalResolveRef = useRef(null);

  const showAlert = useCallback((message, title = '') => {
    return new Promise((resolve) => {
      modalResolveRef.current = resolve;
      setModalState({ type: 'alert', message, title });
    });
  }, []);

  const showConfirm = useCallback((message, title = '', confirmLabel = 'Confirm', destructive = false) => {
    return new Promise((resolve) => {
      modalResolveRef.current = resolve;
      setModalState({ type: 'confirm', message, title, confirmLabel, destructive });
    });
  }, []);

  const handleModalClose = useCallback((result) => {
    if (modalResolveRef.current) modalResolveRef.current(result);
    modalResolveRef.current = null;
    setModalState(null);
  }, []);

  // Fix mobile viewport height
  useEffect(() => {
    const setAppHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    return () => window.removeEventListener('resize', setAppHeight);
  }, []);

  // Load data from Firestore when user is authenticated
  const loadAppData = useCallback(async () => {
    try {
      const [fetchedUsers, fetchedPosts, fetchedEvents] = await Promise.all([
        firestoreService.getUsers(),
        firestoreService.getPosts(),
        firestoreService.getEvents(),
      ]);
      setUsers(fetchedUsers);
      setPosts(fetchedPosts);
      setEvents(fetchedEvents);

      try {
        const filters = await firestoreService.getWordFilters();
        setWordFilters(filters);
      } catch (e) {
        console.log('Word filters not accessible or not set');
      }

      try {
        const [fetchedReports, fetchedModerators, fetchedDisputes] = await Promise.all([
          firestoreService.getReports(),
          firestoreService.getModerators(),
          firestoreService.getDisputes(),
        ]);
        setReports(fetchedReports);
        setModerators(fetchedModerators);
        setDisputes(fetchedDisputes);
      } catch (adminErr) {
        console.log('Admin data not accessible (expected for regular users)');
      }
    } catch (err) {
      console.error('Error loading app data:', err);
    }
  }, []);

  // Set up real-time listeners
  const setupListeners = useCallback((userId) => {
    unsubscribesRef.current.forEach(unsub => unsub());
    unsubscribesRef.current = [];

    const unsubPosts = firestoreService.onPostsChange((newPosts) => setPosts(newPosts));
    const unsubEvents = firestoreService.onEventsChange((newEvents) => setEvents(newEvents));
    const unsubConvs = firestoreService.onConversationsChange(userId, (newConvs) => setConversations(newConvs));
    const unsubNotifs = firestoreService.onNotificationsChange(userId, (newNotifs) => {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && prevNotifIdsRef.current.size > 0) {
        for (const notif of newNotifs) {
          if (!prevNotifIdsRef.current.has(notif.id) && !notif.read) {
            const category = NOTIFICATION_CATEGORY_MAP[notif.type];
            const userDoc = JSON.parse(localStorage.getItem('currentUser') || '{}');
            const prefs = userDoc.notificationPreferences || DEFAULT_NOTIFICATION_PREFS;
            if (!category || prefs[category] !== false) {
              try {
                if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                  navigator.serviceWorker.ready.then(reg => {
                    reg.showNotification('Loft', { body: notif.message, tag: notif.id, badge: '/favicon.ico' });
                  }).catch(() => {});
                } else {
                  new Notification('Loft', { body: notif.message, tag: notif.id });
                }
              } catch (e) { /* silent */ }
            }
          }
        }
      }
      prevNotifIdsRef.current = new Set(newNotifs.map(n => n.id));
      setNotifications(newNotifs);
    });
    const unsubGroups = firestoreService.onGroupsChange(userId, (newGroups) => setGroups(newGroups));
    const unsubSwings = firestoreService.onSwingsChange((newSwings) => setSwings(newSwings));

    unsubscribesRef.current = [unsubPosts, unsubEvents, unsubConvs, unsubNotifs, unsubGroups, unsubSwings];
  }, []);

  // Register service worker (PWA + push notifications)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then((reg) => {
        // When a new SW is waiting, reload to activate it
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available — activate immediately
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      }).catch(() => {});

      // Reload page when a new SW takes control
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) { refreshing = true; window.location.reload(); }
      });
    }
  }, []);

  useEffect(() => {
    const unsubAuth = onAuthChange(async (user) => {
      try {
        if (user) {
          setCurrentUser(user);
          if (user.profileIncomplete || user.status === 'banned' || user.status === 'suspended') {
            return;
          }
          await loadAppData();
          try {
            const [convs, notifs, userGroups] = await Promise.all([
              firestoreService.getConversations(user.id),
              firestoreService.getNotifications(user.id),
              firestoreService.getGroups(user.id),
            ]);
            setConversations(convs);
            setNotifications(notifs);
            setGroups(userGroups);
          } catch (err) {
            console.error('Error loading user data:', err);
          }
          setupListeners(user.id);
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        console.error('Error in auth change handler:', err);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      unsubscribesRef.current.forEach(unsub => unsub());
    };
  }, [loadAppData, setupListeners]);

  const login = async (email, password) => {
    try {
      const user = await loginUser(email, password);
      return true;
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/too-many-requests') {
        showAlert('Too many failed attempts. Please try again later.', 'Error');
      } else if (code.startsWith('auth/')) {
        showAlert('Invalid username/password combination.', 'Error');
      } else {
        showAlert('Login failed. Please try again.', 'Error');
      }
      return false;
    }
  };

  const viewUserProfile = (userId) => {
    setView(`user-profile-${userId}`);
  };

  const toggleFollow = async (userId) => {
    const isFollowing = currentUser.following?.includes(userId);
    const updatedFollowing = isFollowing
      ? (currentUser.following || []).filter(id => id !== userId)
      : [...(currentUser.following || []), userId];
    const updatedCurrentUser = { ...currentUser, following: updatedFollowing };

    setCurrentUser(updatedCurrentUser);
    setUsers(prev => prev.map(u => u.id === currentUser.id ? updatedCurrentUser : u));

    try {
      await firestoreService.toggleFollow(currentUser.id, userId);
      if (!isFollowing) {
        firestoreService.createNotification({
          targetUserId: userId,
          fromUserId: currentUser.id,
          type: 'follow',
          message: 'started following you',
        }).catch(err => console.error('Error creating follow notification:', err));
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    }
  };

  const suspendUser = async (userId, reason, duration = null) => {
    setUsers(users.map(u => {
      if (u.id === userId) {
        return { ...u, status: 'suspended', suspensionReason: reason, suspensionDate: new Date().toISOString(), suspensionDuration: duration || 'permanent', suspensionEndDate: duration ? new Date(Date.now() + duration * 24 * 60 * 60 * 1000).toISOString() : 'permanent' };
      }
      return u;
    }));
    if (currentUser && currentUser.id === userId) setCurrentUser(null);
    try { await firestoreService.suspendUser(userId, reason, duration); } catch (err) { console.error('Error suspending user:', err); }
  };

  const unsuspendUser = async (userId) => {
    setUsers(users.map(u => u.id === userId ? { ...u, status: 'active', suspensionReason: null, suspensionDate: null, suspensionDuration: null, suspensionEndDate: null } : u));
    try {
      await firestoreService.unsuspendUser(userId);
      await firestoreService.createNotification({ targetUserId: userId, type: 'account_restored', message: 'Your account has been restored. Your suspension has been lifted.', fromUserId: 'system' });
    } catch (err) { console.error('Error unsuspending user:', err); }
  };

  const banUser = async (userId, reason) => {
    setUsers(users.map(u => u.id === userId ? { ...u, status: 'banned', banReason: reason, banDate: new Date().toISOString() } : u));
    if (currentUser && currentUser.id === userId) setCurrentUser(null);
    try { await firestoreService.banUser(userId, reason); } catch (err) { console.error('Error banning user:', err); }
  };

  const unbanUser = async (userId) => {
    setUsers(users.map(u => u.id === userId ? { ...u, status: 'active', banReason: null, banDate: null } : u));
    try {
      await firestoreService.unbanUser(userId);
      await firestoreService.createNotification({ targetUserId: userId, type: 'account_restored', message: 'Your account has been restored. Your ban has been lifted.', fromUserId: 'system' });
    } catch (err) { console.error('Error unbanning user:', err); }
  };

  const changeUserRole = async (userId, newRole) => {
    setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    if (currentUser && currentUser.id === userId) setCurrentUser({ ...currentUser, role: newRole });
    try { await firestoreService.changeUserRole(userId, newRole); } catch (err) { console.error('Error changing role:', err); }
  };

  const handleSwingReply = async (swingOwnerId, messageText) => {
    if (!currentUser || !messageText.trim()) return;
    let conv = conversations.find(c =>
      !c.isGroup &&
      c.participants?.includes(currentUser.id) &&
      c.participants?.includes(swingOwnerId)
    );
    if (!conv) {
      conv = await firestoreService.createConversation({
        participants: [currentUser.id, swingOwnerId],
        isGroup: false,
      });
      setConversations(prev => [...prev, conv]);
    }
    await firestoreService.sendMessage({
      conversationId: conv.id,
      senderId: currentUser.id,
      content: messageText.trim(),
    });
  };

  const contextValue = {
    currentUser, setCurrentUser, users, setUsers, posts, setPosts, events, setEvents,
    conversations, setConversations, messages, setMessages, reports, setReports,
    moderators, setModerators, view, setView, videoFiles, setVideoFiles, imageFiles, setImageFiles,
    showCreatePost, setShowCreatePost, notifications, setNotifications, wordFilters, setWordFilters,
    groups, setGroups, activeGame, setActiveGame, selectedGameId, setSelectedGameId,
    messagesKey, setMessagesKey,
    theme, effectiveTheme, setTheme,
    disputes, setDisputes,
    login, viewUserProfile, toggleFollow, suspendUser, unsuspendUser, banUser, unbanUser, changeUserRole,
    showAlert, showConfirm,
    searchNav, setSearchNav,
    swings, setSwings, handleSwingReply,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#16a34a]"></div>
          <p className="mt-4 text-white">Loading Loft...</p>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      <style>{`
        a {
          font-weight: bold;
        }
      `}</style>
      {view === 'action' ? <AuthActionScreen onBackToLogin={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }} /> :
       view === 'legal' ? <div className="h-full overflow-y-auto"><LegalView onBack={() => window.history.back()} /></div> :
       view === 'terms' ? <div className="h-full overflow-y-auto"><TermsOfUseView onBack={() => window.history.back()} /></div> :
       view === 'privacypolicy' ? <div className="h-full overflow-y-auto"><PrivacyPolicyView onBack={() => window.history.back()} /></div> :
       view === 'cookies' ? <div className="h-full overflow-y-auto"><CookiesPolicyView onBack={() => window.history.back()} /></div> :
       !currentUser ? (maintenanceConfig?.enabled ? <MaintenancePage /> : <LoginScreen />) :
        currentUser.profileIncomplete ? <ProfileCompletionScreen googleData={currentUser} /> :
        (currentUser.status === 'banned' || currentUser.status === 'suspended')
          ? <RestrictedScreen />
          : maintenanceConfig?.enabled && !['admin', 'superadmin', 'moderator'].includes(currentUser.role)
          ? <MaintenancePage />
          : <MainApp />}

      <CookieConsentBanner />

      {/* Custom Modal (replaces browser alert/confirm) */}
      {modalState && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4" onClick={() => handleModalClose(modalState.type === 'alert' ? true : false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5">
              {modalState.title && (
                <h3 className="text-lg font-bold text-white mb-2">{modalState.title}</h3>
              )}
              <p className="text-gray-300 text-sm leading-relaxed">{modalState.message}</p>
            </div>
            <div className="flex border-t border-gray-700">
              {modalState.type === 'confirm' && (
                <button
                  onClick={() => handleModalClose(false)}
                  className="flex-1 py-3 text-sm font-medium text-gray-400 hover:bg-gray-800 rounded-bl-xl transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => handleModalClose(true)}
                className={`flex-1 py-3 text-sm font-bold rounded-br-xl transition-colors ${
                  modalState.type === 'confirm' && modalState.destructive
                    ? 'text-red-400 hover:bg-red-900/30 border-l border-gray-700'
                    : modalState.type === 'confirm'
                    ? 'text-[#16a34a] hover:bg-green-900/30 border-l border-gray-700'
                    : 'text-[#16a34a] hover:bg-green-900/30 rounded-bl-xl'
                }`}
              >
                {modalState.type === 'confirm' ? (modalState.confirmLabel || 'Confirm') : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppContext.Provider>
  );
};

export default App;
